import styles from './index.module.css'
import {
  convertUserDropdownObjectToLocked,
  convertUserDropdownObjectToUnlocked,
} from '@disco/utils/user'
import { isEqual } from 'lodash'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { GroupId, NestedChannelGroup } from 'types/group'
import type { DropdownOption } from 'types/ui'
import type { User, UserId } from 'types/user'
import { updateGroup } from '@disco/state-management/actions/GroupActions'
import Row from 'components/common/row'
import UserSelector from 'components/common/user-selector'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Label from 'components/ui/label'
import LoadingIndicator from 'components/ui/loading-indicator'
import { RadioButton, RadioGroup } from 'components/ui/radio'
import { loadManagerAndMemberVisibilityData } from './utils'
type UpdatedData = {
  managers: Array<UserId | number | string>
  members: Array<UserId | number | string>
}
type InternalShareChannelFormProps = {
  user: User
  group: NestedChannelGroup
  isSubfolder: boolean
  parentGroup: NestedChannelGroup
  onSubmit: (error: string, data: UpdatedData) => void
  onClose: () => void
  updateGroup: typeof updateGroup
  loadGroup: (arg0: GroupId) => Promise<any>
}
type LoadManagerAndMemberVisibilityData = {
  chosenManagers: DropdownOption[]
  chosenMembers: DropdownOption[]
  hiddenManagers: UserId[]
  hiddenMembers: UserId[]
  allUsersAreMembers: boolean
  users: User[]
}

const InternalTeamShareForm = (
  props: InternalShareChannelFormProps,
): React.ReactNode => {
  const [formState, setFormState] =
    useState<LoadManagerAndMemberVisibilityData>({
      chosenManagers: [],
      chosenMembers: [],
      hiddenManagers: [],
      hiddenMembers: [],
      allUsersAreMembers: true,
      users: [],
    })
  const managerVisibilityRef = useRef(null)
  const memberVisibilityRef = useRef(null)
  const [submitting, setSubmitting] = useState(false)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()
  const {
    chosenManagers,
    chosenMembers,
    hiddenManagers,
    hiddenMembers,
    allUsersAreMembers,
    users,
  } = formState
  const MEMBER_VISIBILITY = [
    {
      value: true,
      label: 'All users',
    },
    {
      value: false,
      label: 'Selected users',
    },
  ]
  useEffect(() => {
    const { group, user, parentGroup, loadGroup } = props
    const data = {
      groupId: group.id,
      currentUser: user,
      parentGroup,
    }

    const fetchData = async () => {
      const result = await loadManagerAndMemberVisibilityData({
        data,
        loadGroup,
      })
      if (!result) return
      memberVisibilityRef.current = result.allUsersAreMembers
        ? []
        : [...result.chosenMembers]
      managerVisibilityRef.current = result.chosenManagers
      setFormState(prevState => ({
        ...prevState,
        chosenManagers: result.chosenManagers,
        chosenMembers: result.chosenMembers,
        hiddenManagers: result.hiddenManagers,
        hiddenMembers: result.hiddenMembers,
        allUsersAreMembers: result.allUsersAreMembers,
        users: result.businessUsers,
      }))
      setIsLoaded(result.businessUsersLoaded)
    }

    fetchData()
  }, [props.loadGroup, props.group.id, props.parentGroup])

  const onMembersSwitch = () => {
    setFormState(prevState => ({
      ...prevState,
      allUsersAreMembers: !allUsersAreMembers,
      chosenMembers:
        allUsersAreMembers && isLoaded && chosenMembers.length === 0
          ? chosenManagers.map(m => convertUserDropdownObjectToLocked(m))
          : chosenMembers,
      hiddenMembers:
        allUsersAreMembers && isLoaded && hiddenMembers.length === 0
          ? hiddenManagers
          : hiddenMembers,
    }))
  }

  const managersMessage = errors.chosenManagers
    ? 'Please add at least one manager'
    : null

  const onMemberSelect = value => {
    setFormState(prevState => ({ ...prevState, chosenMembers: value }))
  }

  const onManagerSelect = value => {
    setFormState(prevState => ({ ...prevState, chosenManagers: value }))
    const managersNotIncludedToMembersList = value.filter(manager => {
      return !chosenMembers.some(member => {
        return member.value === manager.value
      })
    })
    let selectedChosenMembers = chosenMembers

    if (managersNotIncludedToMembersList.length > -1) {
      selectedChosenMembers = [
        ...selectedChosenMembers,
        ...managersNotIncludedToMembersList,
      ]
    }

    const updatedUsers = selectedChosenMembers.map(member => {
      const lockedUser = value.some(manager => manager.value === member.value)
      return lockedUser
        ? convertUserDropdownObjectToLocked(member)
        : convertUserDropdownObjectToUnlocked(member)
    })
    setFormState(prevState => ({ ...prevState, chosenMembers: updatedUsers }))
  }

  const getValidation = useCallback(() => {
    register('name', {
      required: true,
    })
    setValue('name', props.group.name)
    register('selectedManagers', {
      required: true,
    })
    setValue('selectedManagers', [...hiddenManagers, ...chosenManagers])
  }, [chosenManagers])
  useEffect(() => {
    getValidation()
  }, [getValidation])

  const validateData = data => {
    setSubmitting(true)
    if (!data) return
    const members = allUsersAreMembers
      ? []
      : [...hiddenMembers, ...chosenMembers.map(member => member.value)]
    const managers = [
      ...hiddenManagers,
      ...chosenManagers.map(manager => manager.value),
    ]
    const updatedData = {
      managers,
      members,
    }
    // @ts-expect-error
    return props.onSubmit(errors.selectedManagers, updatedData)
  }

  const handleFormSubmit = async event => {
    event.preventDefault()

    try {
      await handleSubmit(validateData)()
    } catch (error) {
      console.error(error)
    }

    memberVisibilityRef.current = allUsersAreMembers ? [] : [...chosenMembers]
    managerVisibilityRef.current = chosenManagers
    setSubmitting(false)
    setIsDisabled(!submitting)
  }

  const teamMembersAdded = useCallback(() => {
    const memberVisibility = allUsersAreMembers ? [] : [...chosenMembers]
    const managerVisibility = chosenManagers
    const visibilityUpdated =
      isEqual(memberVisibilityRef.current, memberVisibility) &&
      isEqual(managerVisibilityRef.current, managerVisibility)
    setIsDisabled(visibilityUpdated)
  }, [chosenManagers, chosenMembers, allUsersAreMembers])
  useEffect(() => {
    teamMembersAdded()
  }, [teamMembersAdded])
  return (
    <div>
      {!isLoaded ? (
        <div className={styles.InternalTeamShare__LoadingIndicator}>
          <LoadingIndicator />
        </div>
      ) : (
        <form
          onSubmit={e => handleFormSubmit(e)}
          className={styles.InternalTeamShare__FormContainer}
        >
          <Row columnsSize={[12]} marginBottom="small">
            <div>
              <Label caption="Select channel managers" />
              <UserSelector
                placeholder="Choose managers"
                users={users}
                chosenUsers={chosenManagers}
                usersLoading={!isLoaded}
                onUserSelect={onManagerSelect}
              />
              {/* @ts-expect-error  */}
              {errors.result?.chosenManagers && (
                <div className="warning-block">{managersMessage}</div>
              )}
            </div>
          </Row>
          <Row columnsSize={[12]} marginBottom="small">
            <div className={styles.InternalTeamShare__FormContainer}>
              <Label caption="Make channel visible to" />
              <RadioGroup
                onChange={onMembersSwitch}
                value={allUsersAreMembers}
                className={styles.InternalTeamShare__RadioContainer}
              >
                {MEMBER_VISIBILITY.map(option => (
                  <RadioButton
                    key={option.label}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </RadioGroup>

              {isLoaded && !allUsersAreMembers && (
                <UserSelector
                  placeholder="Choose members"
                  users={users}
                  chosenUsers={
                    allUsersAreMembers
                      ? []
                      : chosenMembers.filter(
                          member => member.value !== props.user.id,
                        )
                  }
                  usersLoading={!isLoaded}
                  onUserSelect={onMemberSelect}
                  disabled={allUsersAreMembers}
                />
              )}
            </div>
          </Row>
          <ButtonContainer className={styles.InternalTeamShare_ButtonContainer}>
            <Button
              appearance="subtle"
              caption="Cancel"
              onClick={props.onClose}
            />
            <Button
              appearance="primary"
              caption="Save"
              type="submit"
              disabled={isDisabled}
              progressIndicator={submitting}
            />
          </ButtonContainer>
        </form>
      )}
    </div>
  )
}

export default InternalTeamShareForm
